import {
  EProjectType,
  ESdChangeReason,
  IDisplayItem,
  IKuikaStyle,
  IStylingShadow,
  InvisibleStylePart,
  Visibility
} from "@kuika/kuika-cl-model";
import { Form } from "antd/es";
import { FormInstance } from "antd/es/form";
import classNames from "classnames";
import { Guid } from "guid-typescript";
import _ from "lodash";
import React from "react";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { ColorInputHelper } from "../../../../shared/utility/color-input-helper";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import { ScreenDesignHelper } from "../../../../shared/utility/screen-design-helper";
import { DeleteModal } from "../../../common/delete-modal";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTabs from "../../../ui-kit/kuika-tabs/kuika-tabs";
import KuikaSmallText from "../../../ui-kit/typography/SmallText/SmallText";
import StyleLabel from "./style-label/style-label";
import { ColorPicker } from "./styling-color-picker";
import { StylingSelector } from "./styling-selector/styling-selector";
import { styleSectionNeedsRender } from "./styling-window-helpers";

export interface IDeleteArguments {
  isDeleteStyleModalVisible: boolean;
  deletingStyleName: string;
  performStyleTagOnClose: any | undefined;
}

export interface IShadowComponentState {
  deleteArguments: IDeleteArguments;
  isColorPickerActive: boolean;
}

export interface IShadowComponentProps {
  form: FormInstance<IKuikaStyle>;
  // KOA-15838: true while a style tag is being edited; drives the blue (token)
  // vs orange (locally changed) label colour independently of the form's section
  // id (which a state switch nulls).
  isEditMode?: boolean;
}

export enum EditMode {
  custom = 1,
  edit = 2,
  create = 3
}

class ShadowComponent extends PureSdComponent<IShadowComponentProps & IWithSDProps, IShadowComponentState> {
  styleName: any;

  constructor(props: IShadowComponentProps & IWithSDProps) {
    super(props);
    this.state = {
      deleteArguments: { isDeleteStyleModalVisible: false, deletingStyleName: "", performStyleTagOnClose: undefined },
      isColorPickerActive: false
    };
    this.styleName = React.createRef();
  }

  submitForm = (fieldsValue: IKuikaStyle) => {
    this.props.form.resetFields();
    this.props.form.setFieldsValue(fieldsValue);
    this.props.form.submit();
  };

  new = async (name: string) => {
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    if (!fieldsValue.shadow) {
      return;
    }

    const project = _.cloneDeep(this.props.sd.project);

    let stylingShadows = {} as IStylingShadow;
    stylingShadows.shadowId = Guid.create().toString();

    const selectedDi = this.props.sd.getSelectedDisplayItem(this.props.sd.getScreenDesign());
    const selectedDIItem = selectedDi?.item;
    stylingShadows.name = name;
    stylingShadows.componentType = selectedDIItem?.name;

    stylingShadows.styleType = "custom";

    const formValues = {
      color: fieldsValue.shadow.color,
      x: fieldsValue.shadow.x,
      y: fieldsValue.shadow.y,
      b: fieldsValue.shadow.b,
      s: fieldsValue.shadow.s,
      isInset: fieldsValue.shadow.isInset,
      isVisible: fieldsValue.shadow.isVisible
    };

    stylingShadows = {
      ...stylingShadows,
      ...formValues
    };

    if (!project.stylingShadow) {
      project.stylingShadow = [];
    }
    project.stylingShadow.push(stylingShadows);

    const savedId = stylingShadows.shadowId;

    await this.props.sd.updateProject(project, true, ESdChangeReason.ChangeProjectStyle);

    const updatedFieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    if (updatedFieldsValue.shadow?.shadowIds && savedId) {
      updatedFieldsValue.shadow.shadowIds.push(savedId);
      updatedFieldsValue.shadow.shadowId = savedId;
      updatedFieldsValue.shadow.name = name;
      this.submitForm(updatedFieldsValue);
    }
  };

  edit = (label: string | undefined) => {
    const mode: EditMode = this.getCurrentMode();
    const editDelegate = () => {
      if (label !== undefined && label.length > 0 && this.props?.sd?.project?.stylingShadow) {
        const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
        const shadowBackup = _.clone(fieldsValue.shadow) as IStylingShadow;
        shadowBackup.kuikaid = this.props.sd.selectedKuikaIds[0];
        this.props.sd.setCustomShadowStyleBackup(shadowBackup);
        const stylingShadowDefList = this.props.sd.project.stylingShadow;
        const tempValue = stylingShadowDefList.filter((item) => item.name === label);
        if (tempValue && tempValue.length > 0) {
          this.setShadowStyle(tempValue[0]);
        }
      }
    };
    if (mode === EditMode.custom) {
      editDelegate();
    } else {
      this.setShadowCustomStyle();
      editDelegate();
    }
  };

  duplicate = (label: string | undefined) => {
    const mode: EditMode = this.getCurrentMode();
    const duplicateDelegate = async () => {
      if (label !== undefined && label.length > 0 && this.props?.sd?.project?.stylingShadow) {
        const stylingShadowDefList = this.props.sd.project.stylingShadow;
        const tempValue = stylingShadowDefList.filter((item) => item.name === label);
        if (tempValue && tempValue.length > 0) {
          const copyValue = _.cloneDeep(tempValue[0]);
          copyValue.shadowId = Guid.create().toString();
          copyValue.name = `${label}_copy`;
          copyValue.styleType = "custom";
          delete copyValue.overridden;

          let unique = false;
          while (!unique) {
            var found = false;
            this.props.sd.project.stylingShadow.forEach((stylingShadow) => {
              if (stylingShadow.name === copyValue.name) {
                copyValue.name += "_copy";
                found = true;
              }
            });
            unique = !found;
          }

          const project = _.cloneDeep(this.props.sd.project);
          project.stylingShadow.push(copyValue);
          const savedId = copyValue.shadowId;
          await this.props.sd.updateProject(project, true, ESdChangeReason.ChangeProjectStyle);
          const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
          if (fieldsValue.shadow?.shadowIds && savedId) {
            fieldsValue.shadow.shadowIds.push(savedId);
            this.submitForm(fieldsValue);
          }
        }
      }
    };
    if (mode === EditMode.custom) {
      duplicateDelegate();
    } else {
      this.setShadowCustomStyle();
      duplicateDelegate();
    }
  };

  removeFromComponent = (performTagOnClose: any) => {
    if (performTagOnClose) {
      performTagOnClose();
    }
  };

  deleteFromProjectConfirmation = (label: string | undefined, performTagOnClose: any) => {
    if (label) {
      this.setState({
        deleteArguments: {
          deletingStyleName: label,
          isDeleteStyleModalVisible: true,
          performStyleTagOnClose: performTagOnClose
        }
      });
    }
  };

  isDefaultStying = (styingId: string) => {
    let found = false;
    this.props.sd.defaultStyleProvider?.getDesktopStyles().shadow.map((styling) => {
      if (styling.shadowId?.toLowerCase() == styingId?.toLowerCase() || styling.name == styingId) found = true;
    });
    return found;
  };

  isCurrentComponentsDefaultStying = (styingId: string) => {
    const selectedDi = this.props.sd.getSelectedDisplayItem(this.props.sd.getScreenDesign());

    return (
      styingId?.toLowerCase() ==
      ScreenDesignHelper.getDefaultStylingForMobile(
        selectedDi!,
        "shadow",
        this.props.sd.defaultStyleProvider!
      )?.toLowerCase()
    );
  };

  deleteFromProject = async () => {
    const { deletingStyleName, performStyleTagOnClose } = this.state.deleteArguments;

    if (!deletingStyleName) {
      return;
    }

    const currentShadow = this.props.sd.project.stylingShadow.find((item) => item.name === deletingStyleName);

    if (!currentShadow) {
      KMainFunctions.displayWarningNotification("Style not found.");
      return;
    }

    const currentShadowId: any = currentShadow.shadowId;

    const isDefault = this.isDefaultStying(currentShadowId);

    if (isDefault) {
      KMainFunctions.displayWarningNotification(
        "You cannot remove default styling from the project. If you wish, you can remove it from this component."
      );
      return;
    }

    const deleteDelegate = async () => {
      const project = _.cloneDeep(this.props.sd.project);

      project.stylingShadow = project.stylingShadow.filter((item) => item.name !== deletingStyleName);

      await this.props.sd.updateProject(project, true, ESdChangeReason.ChangeProjectStyle);

      const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
      if (fieldsValue.shadow?.shadowIds) {
        fieldsValue.shadow.shadowIds = fieldsValue.shadow.shadowIds.filter((id) => id !== currentShadowId);
        this.submitForm(fieldsValue);
      }

      if (performStyleTagOnClose) {
        performStyleTagOnClose();
      }

      this.setState({
        deleteArguments: {
          isDeleteStyleModalVisible: false,
          deletingStyleName: "",
          performStyleTagOnClose: undefined
        }
      });
    };

    deleteDelegate();
  };

  getCurrentMode = () => {
    let mode: EditMode = EditMode.custom;
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    if (fieldsValue.shadow?.shadowId && fieldsValue.shadow.shadowId.length > 0) {
      if (fieldsValue.shadow.shadowId === "-1") {
        mode = EditMode.create;
      } else {
        mode = EditMode.edit;
      }
    }
    return mode;
  };

  cancel = () => {
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    if (fieldsValue?.shadow) {
      fieldsValue.shadow.shadowId = null;
      fieldsValue.shadow.name = "";
      this.submitForm(fieldsValue);
    }
    this.setShadowCustomStyle();
  };

  save = async (name?: string, _mode?: EditMode) => {
    const mode: EditMode = _mode || this.getCurrentMode();
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();

    if (!fieldsValue.shadow) {
      return;
    }

    if (name && name.trim().length === 0) {
      KMainFunctions.displayWarningNotification("Please write a name.");
      return;
    }

    if (mode === EditMode.create) {
      const isNameExist = this.props.sd.project.stylingShadow
        .map((s) => s.name)
        .includes(name || fieldsValue.shadow.name);
      if (isNameExist === true) {
        KMainFunctions.displayWarningNotification(
          "There cannot be another registration with the same name. Please write a different name."
        );
        return;
      }
    }

    const project = _.cloneDeep(this.props.sd.project);

    let stylingShadows = {} as IStylingShadow;
    if (mode === EditMode.edit) {
      const data = project.stylingShadow;
      const selectedShadow = fieldsValue?.shadow?.shadowId;
      if (selectedShadow && data && fieldsValue.shadow) {
        stylingShadows = data.filter((item) => item.shadowId === selectedShadow)[0];
      }
    }

    const selectedDi = this.props.sd.getSelectedDisplayItem(this.props.sd.getScreenDesign());
    const selectedDIItem = selectedDi?.item;

    if (!fieldsValue.shadow?.x || (fieldsValue.shadow?.x as any) === "") {
      delete fieldsValue.shadow.x;
    }
    if (!fieldsValue.shadow?.y || (fieldsValue.shadow?.y as any) === "") {
      delete fieldsValue.shadow.y;
    }
    if (!fieldsValue.shadow?.b || (fieldsValue.shadow?.b as any) === "") {
      delete fieldsValue.shadow.b;
    }
    if (!fieldsValue.shadow?.s || (fieldsValue.shadow?.s as any) === "") {
      delete fieldsValue.shadow.s;
    }
    // Inset is "true-or-absent": never persist `false`, otherwise the property
    // merge (TS mergeStyle / C# GetMergedStyle) and the panel's hasValue label
    // would treat the outer default as an explicit value.
    if (!fieldsValue.shadow?.isInset) {
      delete fieldsValue.shadow.isInset;
    }

    if (mode === EditMode.create) {
      stylingShadows.shadowId = Guid.create().toString();
    }
    stylingShadows.name = name || fieldsValue.shadow.name;
    stylingShadows.componentType = fieldsValue.shadow.componentType;
    stylingShadows.isVisible = fieldsValue.shadow.isVisible;

    const formStyleValues = {
      color: fieldsValue.shadow.color,
      x: fieldsValue.shadow.x,
      y: fieldsValue.shadow.y,
      b: fieldsValue.shadow.b,
      s: fieldsValue.shadow.s,
      isInset: fieldsValue.shadow.isInset
    };

    if (stylingShadows.styleType !== "custom" && mode === EditMode.edit) {
      const overridden = stylingShadows.overridden || [];

      Object.keys(formStyleValues).forEach((key) => {
        if (!overridden.includes(key)) {
          if (formStyleValues[key] !== stylingShadows[key]) {
            overridden.push(key);
          }
        }
      });

      stylingShadows.overridden = overridden;
    } else {
      stylingShadows.styleType = "custom";
      delete stylingShadows.overridden;
    }

    stylingShadows = {
      ...stylingShadows,
      ...formStyleValues
    };

    if (!project.stylingShadow) {
      project.stylingShadow = [];
    }
    const exsistingPostionStyleIndex = project.stylingShadow.findIndex((p) => p.shadowId === stylingShadows.shadowId);
    if (exsistingPostionStyleIndex >= 0) {
      project.stylingShadow[exsistingPostionStyleIndex] = stylingShadows;
    } else {
      project.stylingShadow.push(stylingShadows);
    }
    if (mode === EditMode.create) {
      const savedId = stylingShadows.shadowId;
      await this.props.sd.updateProject(project, true, ESdChangeReason.ChangeProjectStyle);
      this.clearStylingShadow();
      const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
      if (fieldsValue.shadow?.shadowIds && savedId) {
        fieldsValue.shadow.shadowIds.push(savedId);
        this.submitForm(fieldsValue);
        this.setShadowCustomStyle();
      }
    } else {
      await this.props.sd.updateProject(project, true, ESdChangeReason.ChangeProjectStyle);
      this.setShadowCustomStyle();
    }
  };

  handleSelectedTagRemoved = (label: string) => {
    const sd = this.props.sd.getScreenDesign();
    const di = this.props.sd.getSelectedDisplayItem(sd) as IDisplayItem;
    const compDef = this.props.sd.getComponentDefinition(di.item);
    const defaultShadow = compDef?.defaultStylingShadow;
    const currentShadows = this.props.sd.project.stylingShadow.filter((item) => item.name === label);

    if (label !== undefined && label.length > 0 && this.props?.sd?.project?.stylingShadow) {
      const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
      const stylingShadowDefList = this.props.sd.project.stylingShadow;
      const tempValue = stylingShadowDefList.filter((item) => item.name === label || item.shadowId === label);
      if (tempValue && tempValue.length > 0 && fieldsValue.shadow) {
        if (tempValue[0].shadowId === fieldsValue.shadow.shadowId) {
          this.setShadowCustomStyle();
        }
      }
    }
    return true;
  };

  handleShadowStylingChange = (value: string | number | Array<string | number> | null) => {
    if (!value || !Array.isArray(value) || value.length === 0) return;

    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();

    if (fieldsValue.shadow) {
      fieldsValue.shadow.shadowIds = value as string[];

      this.props.form.setFieldsValue(fieldsValue);

      this.props.form.submit();
    }
  };

  handleInputChange = (field: string, value: any) => {
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    if (fieldsValue.shadow) {
      fieldsValue.shadow[field] = value;
      this.props.form.setFieldsValue({ shadow: fieldsValue.shadow });
      this.props.form.submit();
    }
  };

  handleInsetChange = (value: string) => {
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    if (!fieldsValue.shadow) {
      return;
    }
    // `true` for inner, `undefined` for outer — never `false` (true-or-absent).
    // Use undefined (not delete): setFieldsValue won't clear a key it doesn't
    // see, so the toggle would stick on "inner". The save() guard then strips
    // the undefined field so the persisted model stays clean.
    fieldsValue.shadow.isInset = value === "inner" ? true : undefined;
    this.props.form.setFieldsValue({ shadow: fieldsValue.shadow });
    this.props.form.submit();
  };

  // Inner (inset) box-shadow renders only on web/desktop — native generators
  // never read shadow.isInset, so an inner shadow is a no-op on a mobile-native
  // surface. Hide the Outer/Inner toggle there (parity with the radial/conic
  // gradient gate in styling-color-picker's getAllowAdvancedGradient): show it
  // on Desktop projects or on web screens, hide it otherwise.
  getAllowInsetShadow = (): boolean => {
    const isDesktop: boolean = this.props.sd?.project?.projectType === EProjectType.Desktop;
    const isWebScreen: boolean = this.props.sd?.getIsWebScreen?.() ?? false;
    return isDesktop || isWebScreen;
  };

  setShadowStyle = (val: IStylingShadow) => {
    if (val) {
      const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
      if (fieldsValue?.shadow) {
        fieldsValue.shadow.shadowId = val.shadowId;
        fieldsValue.shadow.name = val.name;
        fieldsValue.shadow.componentType = val.componentType;
        fieldsValue.shadow.isVisible = val.isVisible;
        fieldsValue.shadow.color = val.color;
        fieldsValue.shadow.x = val.x;
        fieldsValue.shadow.y = val.y;
        fieldsValue.shadow.b = val.b;
        fieldsValue.shadow.s = val.s;
        fieldsValue.shadow.isInset = val.isInset;

        this.submitForm(fieldsValue);
      }
    }
  };

  setShadowCustomStyle = () => {
    const { backupCustomShadow } = this.props.sd;
    if (backupCustomShadow?.kuikaid === this.props.sd.selectedKuikaIds[0]) {
      const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
      if (fieldsValue?.shadow && backupCustomShadow) {
        fieldsValue.shadow.shadowId = null;
        fieldsValue.shadow.name = backupCustomShadow.name;
        fieldsValue.shadow.componentType = backupCustomShadow.componentType;
        fieldsValue.shadow.isVisible = backupCustomShadow.isVisible;
        fieldsValue.shadow.color = backupCustomShadow.color;
        fieldsValue.shadow.x = backupCustomShadow.x;
        fieldsValue.shadow.y = backupCustomShadow.y;
        fieldsValue.shadow.b = backupCustomShadow.b;
        fieldsValue.shadow.s = backupCustomShadow.s;
        fieldsValue.shadow.isInset = backupCustomShadow.isInset;
        this.clearProjectTempStyle();
        this.submitForm(fieldsValue);
      } else {
        this.clearStylingShadow();
      }
    } else {
      this.clearProjectTempStyle();
    }
  };

  handleShadowColorClear = () => {
    const values = this.props.form.getFieldValue("shadow");
    if (values) {
      values.color = undefined;
    }
    this.props.form.setFieldsValue({ shadow: values });
    this.props.form.submit();
  };

  clearProjectTempStyle = async () => {
    const { projectTempStyles } = this.props.sd;
    projectTempStyles.tempStylingShadow = undefined;
    this.props.sd.setProjectTempStyles(projectTempStyles);
    this.props.sd.setCustomShadowStyleBackup(undefined);
  };

  clearStylingShadow = () => {
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    if (fieldsValue?.shadow) {
      fieldsValue.shadow.shadowId = null;
      fieldsValue.shadow.name = undefined;
      fieldsValue.shadow.isVisible = undefined;
      fieldsValue.shadow.color = undefined;
      fieldsValue.shadow.x = undefined;
      fieldsValue.shadow.y = undefined;
      fieldsValue.shadow.b = undefined;
      fieldsValue.shadow.s = undefined;
      fieldsValue.shadow.isInset = undefined;
    }
    this.submitForm(fieldsValue);
  };

  componentDidUpdate = (prevProps: IShadowComponentProps & IWithSDProps, prevState: IShadowComponentState) => {
    const mode: EditMode = this.getCurrentMode();
    if (mode !== EditMode.custom && prevProps.sd.selectedKuikaIds[0] !== this.props.sd.selectedKuikaIds[0]) {
      this.clearProjectTempStyle();
      this.setShadowCustomStyle();
    }
    if (prevProps.sd.selectedKuikaIds[0] !== this.props.sd.selectedKuikaIds[0]) {
    }
  };

  getFriendlyName = (): string => {
    const selectedDi = this.props.sd.getSelectedDisplayItem(this.props.sd.getScreenDesign());
    return selectedDi?.friendlyName || "";
  };

  hasValue = (fieldPath: string[]) => {
    const value = this.props.form.getFieldValue(fieldPath);
    if (value === "transparent") {
      return false;
    }
    return value !== undefined && value !== null && value !== "";
  };

  getStyleClassNames = (fieldPaths: string[], baseClassName?: string) => {
    const mode: EditMode = this.getCurrentMode();
    const hasAnyValue = fieldPaths.some((path) => this.hasValue(["shadow", path]));
    // KOA-15838: editing a style tag means the value IS a token value → blue
    // (`--has-value`), not the orange "changed" dot — even after a state switch
    // nulls the section id (which would flip getCurrentMode to custom).
    const isTokenValue: boolean = this.props.isEditMode === true || mode === EditMode.edit;

    return classNames(baseClassName, {
      "kuika-styling-panel__label--has-value": isTokenValue && hasAnyValue,
      "kuika-styling-panel__label--changed": !isTokenValue && hasAnyValue
    });
  };

  render() {
    const friendlyName = this.getFriendlyName();
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    const selectedDi = this.props.sd.getSelectedDisplayItem(this.props.sd.getScreenDesign());
    let selectedItem = {};
    if (selectedDi) {
      selectedItem = selectedDi!.item;
    }

    const stylingVersionMenu = (label: string, performTagOnClose: any) => {
      const isDefaultStyling = this.isDefaultStying(label);
      return [
        {
          key: "1",
          label: "Edit style",
          icon: <KuikaSvg width={16} height={16} name="settings" type="sidebar-icons" />,
          onClick: () => this.edit(label)
        },
        {
          key: "2",
          label: "Duplicate",
          icon: <KuikaSvg width={16} height={16} name="copy-plus" />,
          onClick: () => this.duplicate(label)
        },
        {
          key: "3",
          label: `Remove from '${friendlyName}'`,
          icon: <KuikaSvg width={16} height={16} name="x" />,
          onClick: () => this.removeFromComponent(performTagOnClose)
        },
        {
          key: "4",
          label: "Delete from project",
          icon: <KuikaSvg width={16} height={16} name="trash" />,
          onClick: () => this.deleteFromProjectConfirmation(label, performTagOnClose),
          danger: true,
          hidden: isDefaultStyling
        }
      ];
    };

    let mode: EditMode = this.getCurrentMode();

    const styleList = (
      <StylingSelector
        formItemName={["shadow", "shadowIds"]}
        stylingList={this.props?.sd?.project?.stylingShadow}
        idField="shadowId"
        handleSelectedTagRemoved={this.handleSelectedTagRemoved}
        isDefaultStyling={this.isDefaultStying}
        isCurrentComponentDefaultStyling={this.isCurrentComponentsDefaultStying}
        stylingVersionMenu={stylingVersionMenu}
        defaultStylingRegex={/KUIKA-[a-zA-Z]+-DEFAULT-SHADOW(#[a-zA-Z]+#)*$/gm}
        new={this.new}
        cancel={this.cancel}
        save={this.save}
        edit={this.edit}
        mode={mode}
        form={this.props.form}
        onChange={this.handleShadowStylingChange}
        hide
      />
    );

    const form = (
      <>
        <Form.Item name={["shadow", "shadowId"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["shadow", "componentType"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["shadow", "name"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["shadow", "kuikaid"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["shadow", "color"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["shadow", "x"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["shadow", "y"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["shadow", "b"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["shadow", "s"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["shadow", "isInset"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["shadow", "isVisible"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["shadow", "name"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["shadow", "shadowIds"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["shadow", "componentType"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <div
          className={classNames("kuika-styling-panel__shadow-form", {
            "kuika-styling-panel__editing": mode === EditMode.edit
          })}
        >
          <div className="kuika-flex--gap-s kuika-flex-column">
            {this.getAllowInsetShadow() && (
              <div className="kuika-styling-panel__form-field-wrapper">
                <StyleLabel
                  fieldPaths={["isInset"]}
                  section="isInset"
                  hasValue={this.hasValue(["shadow", "isInset"])}
                  className={this.getStyleClassNames(["isInset"])}
                  onReset={this.resetSectionValues}
                  disabled={this.isStyleHidden(InvisibleStylePart.Shadow_Structure)}
                >
                  Type
                </StyleLabel>
                <div className="kuika-styling-panel__input-wrapper kuika-styling-panel__input-wrapper--shadow">
                  <Form.Item noStyle shouldUpdate>
                    {() => (
                      <KuikaTabs
                        size="sm"
                        options={[
                          { value: "outer", label: "Outer" },
                          { value: "inner", label: "Inner" }
                        ]}
                        value={this.props.form.getFieldValue(["shadow", "isInset"]) ? "inner" : "outer"}
                        onChange={this.handleInsetChange}
                        disabled={this.isStyleHidden(InvisibleStylePart.Shadow_Structure)}
                        dataTestId="shadow-inset-tabs"
                      />
                    )}
                  </Form.Item>
                </div>
              </div>
            )}

            <div className="kuika-styling-panel__form-field-wrapper">
              <StyleLabel
                fieldPaths={["x"]}
                section="x"
                hasValue={this.hasValue(["shadow", "x"])}
                className={this.getStyleClassNames(["x"])}
                onReset={this.resetSectionValues}
                disabled={this.isStyleHidden(InvisibleStylePart.Shadow_Structure)}
              >
                X
              </StyleLabel>
              <div className="kuika-styling-panel__input-wrapper kuika-styling-panel__input-wrapper--shadow">
                <Form.Item name={["shadow", "x"]} noStyle>
                  <KuikaInput
                    disabled={this.isStyleHidden(InvisibleStylePart.Shadow_Structure)}
                    type="number"
                    size="sm"
                    placeholder="0"
                    rightUnit="px"
                    precision={0}
                    onBlur={(e) => this.handleInputChange("x", e.target.value)}
                    min={0}
                    onKeyDown={(e) => (KMainFunctions.allowOnlyNumbers(e) ? true : e.preventDefault())}
                  />
                </Form.Item>
              </div>
            </div>

            <div className="kuika-styling-panel__form-field-wrapper">
              <StyleLabel
                fieldPaths={["y"]}
                section="y"
                hasValue={this.hasValue(["shadow", "y"])}
                className={this.getStyleClassNames(["y"])}
                onReset={this.resetSectionValues}
                disabled={this.isStyleHidden(InvisibleStylePart.Shadow_Structure)}
              >
                Y
              </StyleLabel>
              <div className="kuika-styling-panel__input-wrapper kuika-styling-panel__input-wrapper--shadow">
                <Form.Item name={["shadow", "y"]} noStyle>
                  <KuikaInput
                    disabled={this.isStyleHidden(InvisibleStylePart.Shadow_Structure)}
                    type="number"
                    size="sm"
                    placeholder="0"
                    rightUnit="px"
                    precision={0}
                    onBlur={(e) => this.handleInputChange("y", e.target.value)}
                    min={0}
                    onKeyDown={(e) => (KMainFunctions.allowOnlyNumbers(e) ? true : e.preventDefault())}
                  />
                </Form.Item>
              </div>
            </div>

            <div className="kuika-styling-panel__form-field-wrapper">
              <StyleLabel
                fieldPaths={["b"]}
                section="blur"
                hasValue={this.hasValue(["shadow", "b"])}
                className={this.getStyleClassNames(["b"])}
                onReset={this.resetSectionValues}
                disabled={this.isStyleHidden(InvisibleStylePart.Shadow_Structure)}
              >
                Blur
              </StyleLabel>
              <div className="kuika-styling-panel__input-wrapper kuika-styling-panel__input-wrapper--shadow">
                <Form.Item name={["shadow", "b"]} noStyle>
                  <KuikaInput
                    disabled={this.isStyleHidden(InvisibleStylePart.Shadow_Structure)}
                    type="number"
                    size="sm"
                    placeholder="0"
                    rightUnit="px"
                    precision={0}
                    onBlur={(e) => this.handleInputChange("b", e.target.value)}
                    min={0}
                    onKeyDown={(e) => (KMainFunctions.allowOnlyNumbers(e) ? true : e.preventDefault())}
                  />
                </Form.Item>
              </div>
            </div>

            <div className="kuika-styling-panel__form-field-wrapper">
              <StyleLabel
                fieldPaths={["s"]}
                section="spread"
                hasValue={this.hasValue(["shadow", "s"])}
                className={this.getStyleClassNames(["s"])}
                onReset={this.resetSectionValues}
                disabled={this.isStyleHidden(InvisibleStylePart.Shadow_Structure)}
              >
                Spread
              </StyleLabel>
              <div className="kuika-styling-panel__input-wrapper kuika-styling-panel__input-wrapper--shadow">
                <Form.Item name={["shadow", "s"]} noStyle>
                  <KuikaInput
                    disabled={this.isStyleHidden(InvisibleStylePart.Shadow_Structure)}
                    type="number"
                    size="sm"
                    placeholder="0"
                    rightUnit="px"
                    precision={0}
                    onBlur={(e) => this.handleInputChange("s", e.target.value)}
                    min={0}
                    onKeyDown={(e) => (KMainFunctions.allowOnlyNumbers(e) ? true : e.preventDefault())}
                  />
                </Form.Item>
              </div>
            </div>

            <div className="kuika-styling-panel__form-field-wrapper">
              <StyleLabel
                fieldPaths={["color"]}
                section="color"
                hasValue={this.hasValue(["shadow", "color"])}
                className={this.getStyleClassNames(["color"])}
                onReset={this.resetSectionValues}
                disabled={this.isStyleHidden(InvisibleStylePart.Shadow_Color)}
              >
                Color
              </StyleLabel>
              <div className="kuika-styling-panel__input-wrapper kuika-styling-panel__input-wrapper--shadow">
                <Form.Item noStyle shouldUpdate>
                  <KuikaInput
                    placeholder={!this.props.form.getFieldValue(["shadow", "color"]) ? "TRANSPARENT" : ""}
                    value={KMainFunctions.convertRgbaToHex(this.props.form.getFieldValue(["shadow", "color"]))}
                    disabled={this.isStyleHidden(InvisibleStylePart.Shadow_Color)}
                    className="kuika-styling-panel__color-input"
                    onClick={(e) => this.handleColorPickerClick(e)}
                    onChange={this.handleShadowColorInputChange}
                    leftUnit={
                      this.props.form.getFieldValue(["shadow", "color"]) &&
                      this.props.form.getFieldValue(["shadow", "color"]) !== "transparent" ? (
                        <div
                          onClick={(e) => this.handleLeftUnitClick(e)}
                          style={{
                            width: 24,
                            height: 24,
                            backgroundColor: this.props.form.getFieldValue(["shadow", "color"])
                          }}
                        ></div>
                      ) : (
                        <img
                          onClick={(e) => this.handleLeftUnitClick(e)}
                          src={"/images/styling-panel-icons/transparent.svg"}
                          alt="transparent"
                          className="kuika-styling-panel__transparent-icon"
                        />
                      )
                    }
                    rightUnit={
                      <div
                        className="kuika-styling-panel__color-input-unit"
                        style={{ display: "flex", alignItems: "center", gap: "4px" }}
                      >
                        {this.props.form.getFieldValue(["shadow", "color"]) &&
                          this.props.form.getFieldValue(["shadow", "color"]) !== "transparent" && (
                            <>
                              <KuikaSvg
                                width={16}
                                height={16}
                                name="trash"
                                onClick={this.handleShadowColorClear}
                                pointer
                              />
                              <KuikaSmallText>HEX</KuikaSmallText>
                            </>
                          )}
                      </div>
                    }
                    size="sm"
                  />
                </Form.Item>
              </div>
            </div>
          </div>
        </div>
        <div
          style={{
            display: this.state.isColorPickerActive === true ? "block" : "none"
          }}
          className="kuika_colorPicker"
        >
          <Form.Item name={["shadow", "color"]} valuePropName="color" noStyle>
            <ColorPicker
              onCancel={this.closeColorPicker}
              isColorPickerActive={this.state.isColorPickerActive}
              onChange={this.handleShadowColorChange}
              hideHeaderTabs={true}
            />
          </Form.Item>
        </div>
      </>
    );

    const deleteModal = (
      <DeleteModal
        itemName={this.state.deleteArguments.deletingStyleName}
        triggerModal={this.state.deleteArguments.isDeleteStyleModalVisible}
        deleteItem={() => this.deleteFromProject()}
        deleteType={"style"}
        closeModal={() =>
          this.setState({
            deleteArguments: {
              isDeleteStyleModalVisible: false,
              deletingStyleName: "",
              performStyleTagOnClose: undefined
            }
          })
        }
      />
    );

    return (
      <>
        {deleteModal}
        <div>
          {styleList}
          {form}
        </div>
      </>
    );
  }

  handleColorPickerClick = (event?: any) => {
    event?.stopPropagation();
    this.setState({
      isColorPickerActive: !this.state.isColorPickerActive
    });
  };

  handleLeftUnitClick = (event?: any) => {
    const inputElement = event?.target?.closest(".kuika-styling-panel__input-wrapper")?.querySelector("input");
    if (inputElement) {
      inputElement.focus();
    }
    this.handleColorPickerClick(event);
  };

  handleShadowColorChange = (color: string) => {
    const values = this.props.form.getFieldValue("shadow");
    values.color = color;
    this.props.form.setFieldsValue({ shadow: values });
    this.props.form.submit();
  };

  handleShadowColorInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    ColorInputHelper.handleColorInputChange(e.target.value, this.handleShadowColorChange);
  };

  handleOpacityChange = (e) => {
    const values = this.props.form.getFieldValue("shadow");
    if (!values.color) return;

    values.color = ColorInputHelper.updateColorOpacity(values.color, e.target.value);
    this.props.form.setFieldsValue({ shadow: values });
    this.props.form.submit();
  };

  getOpacityFromColor = (color: string) => {
    return ColorInputHelper.getOpacityFromColor(color);
  };

  closeColorPicker = () => {
    this.setState({
      isColorPickerActive: false
    });
  };

  checkNeedRender = (changeReason: ESdChangeReason): boolean => styleSectionNeedsRender(changeReason);

  getShadowInputValue = () => {
    const values = this.props.form.getFieldValue("shadow");
    if (!values) return "0 0 0 0";

    const x = values.x || 0;
    const y = values.y || 0;
    const blur = values.b || 0;
    const spread = values.s || 0;

    return `${x}px ${y}px ${blur}px ${spread}px`;
  };

  isStyleHidden = (styleName: InvisibleStylePart): boolean => {
    return this.props.sd.getStylesVisibility(styleName) === Visibility.Hidden;
  };

  resetSectionValues = (fields: string[]) => {
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    if (fieldsValue.shadow) {
      fields.forEach((field) => {
        if (fieldsValue.shadow) {
          fieldsValue.shadow[field] = undefined;
        }
      });
      this.submitForm(fieldsValue);
    }
  };
}

const shadowComponent = withSD<IShadowComponentProps>(ShadowComponent);
export { shadowComponent as ShadowComponent };
